import React, { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Table from "../../core/pagination/datatable";
import CommonFooter from "../../core/common/footer/commonFooter";

import RefreshIcon from "../../core/common/tooltip-content/refresh";
import CollapesIcon from "../../core/common/tooltip-content/collapes";
import AddSales from "../../core/modals/purchases/addSales";
import { 
  useDeleteSalesOrderMutation, 
  useGetSalesOrderByIdQuery, 
  useGetSalesOrdersQuery,
  useUpdateSalesOrderMutation 
} from "../../core/redux/api/OrderApi";
import CommonDeleteModal from "../../core/common/modal/commonDeleteModal";
import toast from "react-hot-toast";
import EditSalesOrder from "../../core/modals/purchases/editSales";
import { DatePicker, Input } from "antd";
import { useGetProductListQuery } from "../../core/redux/api/productApi";
import { useGetAllCustomerQuery } from "../../core/redux/api/userApi";
import Select from "react-select";
import TooltipIcons from "./toolTipOFSalesReport";
import { useSelector } from "react-redux";
const SaleReport = () => {
  const isMobile = window.innerWidth < 768;
   const user = useSelector((state) => state.user.user);
  const { data: todaySaleOrder, isLoading: isTodaySaleOrderLoading, refetch: refetchTodaySaleOrders } = useGetSalesOrdersQuery();
  const [deleteSale] = useDeleteSalesOrderMutation();
  const [updateSale, { isLoading: isUpdating }] = useUpdateSalesOrderMutation();
  const { data: productResponse } = useGetProductListQuery();
  const { data: customerResponse } = useGetAllCustomerQuery();
  
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [saleOrderToDelete, setSaleOrderToDelete] = useState(null);
  const [selectedSaleOrderId, setSelectedSaleOrderId] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [totalAmount, setTotalAmount] = useState(0);
  const [dateRange, setDateRange] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedType, setSelectedType] = useState(null);

  const { data: saleOrderData, isLoading: isSaleOrderLoading } = useGetSalesOrderByIdQuery(selectedSaleOrderId, {
    skip: !selectedSaleOrderId,
  });

  // Memoize customer options for Select component
  const customerOptions = useMemo(() => {
    return customerResponse?.map(customer => ({
      label: customer.customerName,
      value: customer._id,
      ...customer
    })) || [];
  }, [customerResponse]);

  // Optimized filter and sum calculation using reduce
  const { filteredData, sum } = useMemo(() => {
    if (!todaySaleOrder) return { filteredData: [], sum: 0 };
    
    return todaySaleOrder.reduce((acc, transaction) => {
      // Date filter check
      if (dateRange?.[0] && dateRange?.[1]) {
        const transactionDate = new Date(transaction.createdAt);
        const startDate = new Date(dateRange[0]);
        const endDate = new Date(dateRange[1]);
        endDate.setDate(endDate.getDate() + 1); // Include full end day
        
        if (transactionDate < startDate || transactionDate >= endDate) {
          return acc;
        }
      }

      // Customer filter check
      if (selectedCustomer) {
        const transactionCustomerId = transaction.customer?._id || transaction.customerId?._id;
        if (transactionCustomerId !== selectedCustomer._id) {
          return acc;
        }
      }

      // Type filter check
      if (selectedType && transaction.type !== selectedType.value) {
        return acc;
      }

      // If we get here, transaction passed all filters
      acc.filteredData.push(transaction);
      acc.sum += parseFloat(transaction.totalAmount) || 0;
      return acc;
    }, { filteredData: [], sum: 0 });
  }, [todaySaleOrder, dateRange, selectedCustomer, selectedType]);

  // Update total amount whenever sum changes
  useEffect(() => {
    setTotalAmount(parseFloat(sum.toFixed(2)));
  }, [sum]);

  const handleDelete = async () => {
    if (!saleOrderToDelete) return;

    try {
      await toast.promise(
        deleteSale(saleOrderToDelete).unwrap(),
        {
          loading: 'Deleting order...',
          success: async () => {
            await refetchTodaySaleOrders();
            setShowDeleteModal(false);
            setSaleOrderToDelete(null);
            return 'Order deleted successfully!';
          },
          error: (err) => {
            if (err.status === 404) {
              refetchTodaySaleOrders();
              return 'Order was already removed';
            }
            return err.data?.message || 'Delete failed';
          }
        }
      );
    } catch (error) {
      console.error("Delete error:", error);
      const message = error?.data?.message || 'Delete failed';
      if (!message.includes("already deleted")) {
        toast.error(message);
      }
    }
  };

  const handleEdit = (record) => {
    setSelectedSaleOrderId(record._id);
    setShowEditModal(true);
  };

  const handleUpdateSalesOrder = async (values) => {
    try {
      await toast.promise(
        updateSale({
          _id: selectedSaleOrderId,
          ...values
        }).unwrap(),
        {
          loading: 'Updating order...',
          success: async () => {
            await refetchTodaySaleOrders();
            setShowEditModal(false);
            setSelectedSaleOrderId(null);
            return 'Order updated successfully!';
          },
          error: (err) => {
            return err.data?.message || 'Update failed';
          }
        }
      );
    } catch (error) {
      console.error("Update error:", error);
      toast.error(error?.data?.message || 'Update failed');
    }
  };

  const columns = [
    {
      title: "Sale Date",
      dataIndex: "saleDate",
      sorter: (a, b) => new Date(a.purchaseDate) - new Date(b.purchaseDate),
      responsive: ['sm', 'md', 'lg', 'xl'],
      render: (date) => {
        // Convert to Date object if it isn't already
        const dateObj = new Date(date);
        
        // Format as English date (e.g., "January 15, 2023")
        return dateObj.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
        
        // Alternative shorter format (e.g., "1/15/2023"):
        // return dateObj.toLocaleDateString('en-US');
      }
    },
    {
      title: "Customer Name",
      dataIndex: "customer",
      render: (customer) => <span>{customer?.customerName}</span>,
      sorter: (a, b) => a.customer?.customerName.length - b.customer?.customerName.length,
      responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
    },
    {
      title: "Product Name",
      dataIndex: "product",
      render: (product) => <span>{product?.productName}</span>,
      sorter: (a, b) => a.product?.productName.length - b.product?.productName.length,
      responsive: ['sm', 'md', 'lg', 'xl'],
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      sorter: (a, b) => a.quantity - b.quantity,
      responsive: ['sm', 'md', 'lg', 'xl'],
    },
    {
      title: "Unit",
      dataIndex: "unit",
      responsive: ['sm', 'md', 'lg', 'xl'],
    },
    {
      title: "Sale Rate",
      dataIndex: "saleRate",
      sorter: (a, b) => a.saleRate - b.saleRate,
      responsive: ['sm', 'md', 'lg', 'xl'],
    },

    {
      title: "Credit Amount",
      dataIndex: "totalAmount",
      render: (amount) => <span>{parseFloat(amount).toFixed(2)}</span>,
      sorter: (a, b) => a.totalAmount - b.totalAmount,
      responsive: ['sm', 'md', 'lg', 'xl'],
    },
    {
      title: "Note",
      dataIndex: "note",
      render: (text) => text || "-",
      responsive: ['sm', 'md', 'lg', 'xl'],
    },
    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      render: (_, record) => (
        <div className="action-table-data">
          <div className="edit-delete-action">
            <Link
              className="me-2 p-2"
              to="#"
              onClick={(e) => {
                e.preventDefault();
                handleEdit(record);
              }}
            >
              <i data-feather="edit" className="feather-edit"></i>
            </Link>
            <Link
              to="#"
              className="p-2"
              data-bs-toggle="modal"
              data-bs-target="#delete-modal"
              onClick={(e) => {
                e.preventDefault();
                setSaleOrderToDelete(record._id);
              }}
            >
              <i data-feather="trash-2" className="feather-trash-2"></i>
            </Link>
          </div>
        </div>
      ),
      responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
    },
  ];

  const expandable = {
    expandedRowRender: (record) => (
      <div>
         <p><strong>Product Name:</strong> {record.product.productName}</p>
         <p><strong>Customer Name:</strong> {record.customer.customerName}</p>
         <p><strong>Sale Date:</strong> {record.saleDate}</p>
        <p><strong>Quantity:</strong> {record.quantity}</p>
        <p><strong>Unit:</strong> {record.unit}</p>
        <p><strong>Sale Rate:</strong> {record.saleRate}</p>
        <p><strong>Note:</strong> {record.note || "-"}</p>
        <p><strong>Amount:</strong> {parseFloat(record.totalAmount).toFixed(2)}</p>
      </div>
    ),
    rowExpandable: () => true,
  };

const excelOptions = {
    fileName: 'Sales_Report',
    sheetName: 'Sales',
    columns: [
        { key: 'saleDate', header: 'Date' },
        // { key: 'customerName', header: 'Customer' }, // Excluded to match PDF's active columns
        { key: 'productName', header: 'Product' },
        { key: 'quantity', header: 'Qty' },
        { key: 'unitName', header: 'Unit' },
        { key: 'saleRate', header: 'Rate' },
        { key: 'amount', header: 'Amount' },
        // { key: 'note', header: 'Remarks' } // Included if you want remarks in Excel, though commented out in PDF columns
    ],
    data: filteredData.map(receipt => ({
        // Aligning saleDate formatting with PDF
        saleDate: new Date(receipt?.saleDate).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        }),
        // Including customerName as it's in the raw data, but commented out in PDF columns
        customerName: receipt.customer?.customerName || '-',
        productName: receipt.product?.productName || '-',
        quantity: receipt.quantity,
        // Aligning saleRate formatting with PDF
        saleRate: parseFloat(receipt.saleRate).toFixed(2),
        // Aligning amount formatting with PDF
        amount: parseFloat(receipt.totalAmount).toFixed(2),
        // Including note for consistency with PDF data mapping
        note: receipt.note || '-'
    }))
};



        // PDF Options configuration
        const companyName = user?.company || 'Company Name'; // Fallback to default if companyName is not available
        const address = user?.Address || 'Default Address'; // Fallback to default logo if not available
        const state = user?.state || 'Default State'; // Fallback to default state if not availablecons
        const city = user?.city || 'Default City'; // Fallback to default city if not available
        const postalCode = user?.postalCode || '000000'; // Fallback to default postal code if not available
      
        const pdfOptions = {
            fileName: `${companyName}`,
            title: `${companyName}`, // This will be overridden by customContent
            columns: [
                { key: 'saleDate', header: 'Date', width: 35 },
               
                { key: 'productName', header: 'Product', width: 25 },
                { key: 'quantity', header: 'Qty', width: 15 },
                { key: 'unit', header: 'Unit', width: 15 },
                { key: 'saleRate', header: 'Rate', width: 20 },
                { key: 'amount', header: 'Amount', width: 45 },
                 { key: 'Debit', header: 'Transaction Type', width: 28 },
                // { key: 'note', header: 'Remarks', width: 30 }
            ],
            data: filteredData.map(receipt => ({
                saleDate: new Date(receipt?.saleDate).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                }),
                customerName: receipt.customer?.customerName || '-',
                productName: receipt.product?.productName || '-',
                quantity: receipt.quantity,
                unit: receipt.unit || '-', // Assuming unitName is available in the receipt
                Debit: 'Debit', // Assuming type is available in the receipt
                saleRate: parseFloat(receipt.saleRate).toFixed(2),
               amount: new Intl.NumberFormat('en-IN', { 
  minimumFractionDigits: 2,
  maximumFractionDigits: 2 
}).format(parseFloat(receipt.totalAmount)),
                note: receipt.note || '-'
            })),
            
            dateRange: dateRange,
            selectedCustomer: selectedCustomer,
            totalAmount: totalAmount,
            customContent: (doc, startY) => {
                let y = startY + 10; // Initial spacing from top of the page/previous content

                // Company Name (centered)
                doc.setFontSize(22);
                doc.setFont(undefined, 'normal');
                doc.text(`${companyName}`, doc.internal.pageSize.width / 2, y, { align: 'center' });
                y += 8;

                // Main Title: Sales Ledger Report (centered)
                doc.setFontSize(12);
                doc.setFont(undefined, 'bold');
                doc.text(`${address} ${state} ${city} ${postalCode}`, doc.internal.pageSize.width / 2, y, { align: 'center' });
                doc.setFont(undefined, 'normal');
                y += 10; // Reduced space after title

                // Line separator below the main header
                doc.setDrawColor(200, 200, 200); // Light grey line
                doc.line(14, y, doc.internal.pageSize.width - 14, y); // Draw a line from left to right margin
                y += 10; // Space after line

                doc.setFontSize(12);
                doc.setTextColor(50, 50, 50); // Dark grey for report details

                // Date Range (centered just above the table)
                if (dateRange && dateRange[0] && dateRange[1]) {
                    const startDate = new Date(dateRange[0]).toLocaleDateString('en-In', { day: '2-digit', month: '2-digit', year: 'numeric' });
                    const endDate = new Date(dateRange[1]).toLocaleDateString('en-In', { day: '2-digit', month: '2-digit', year: 'numeric' });
                    doc.text(`From Date: ${startDate} To Date ${endDate}`, doc.internal.pageSize.width / 2, y, { align: 'center' });
                } else {
                    doc.text('Date Range: All Dates', doc.internal.pageSize.width / 2, y, { align: 'center' });
                }
                y += 7;

                // Customer (left-aligned)
                if (selectedCustomer) {
                    doc.text(`Customer: ${selectedCustomer.customerName}`, 14, y);
                } else {
                    doc.text('Customer: All Customers', 14, y);
                }
                y += 15; // Space before table starts

                return y; // Return the new Y position for the table to start
            },
            footer: (doc, finalY) => {
                doc.setFontSize(10);
                doc.setTextColor(100); // Grey color for footer text

                // Generated On
                doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, finalY);

                // Page Numbers
                const pageCount = doc.internal.getNumberOfPages();
                for (let i = 1; i <= pageCount; i++) {
                    doc.setPage(i);
                    doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width - 30, doc.internal.pageSize.height - 10, { align: 'right' });
                }
            }
        };
  console.log(
  pdfOptions.data.map(item => item?.unit?.unitName)
);
// const pdfOptions = {
//   fileName: 'Sales_Ledger_Report',
//   title: 'Sales Ledger Report',
//   columns: [
//     { key: 'saleDate', header: 'Date', width: '15%' },
//     { key: 'customerName', header: 'Customer', width: '20%' },
//     { key: 'productName', header: 'Product', width: '20%' },
//     { key: 'quantity', header: 'Qty', width: '10%' },
//     { key: 'saleRate', header: 'Rate', width: '10%' },
//     { key: 'amount', header: 'Amount', width: '15%' },
//     { key: 'note', header: 'Remarks', width: '20%' }
//   ],
//   data: filteredData?.map(receipt => ({
//     saleDate: new Date(receipt?.saleDate).toLocaleDateString('en-US', {
//       year: 'numeric',
//       month: 'short',
//       day: 'numeric'
//     }),
//     customerName: receipt.customer?.customerName || '-',
//     productName: receipt.product?.productName || '-',
//     quantity: receipt.quantity,
//     saleRate: parseFloat(receipt.saleRate).toFixed(2),
//     amount: parseFloat(receipt.totalAmount).toFixed(2),
//     note: receipt.note || '-'
//   })),
//   customContent: (doc) => {
//     doc.setFontSize(12);
    
//     // Add date range if selected
//     if (dateRange && dateRange[0] && dateRange[1]) {
//       const startDate = new Date(dateRange[0]).toLocaleDateString();
//       const endDate = new Date(dateRange[1]).toLocaleDateString();
//       doc.text(`Date Range: ${startDate} to ${endDate}`, 14, 30);
//     } else {
//       doc.text('Date Range: All Dates', 14, 30);
//     }
    
//     // Add customer if selected
//     if (selectedCustomer) {
//       doc.text(`Customer: ${selectedCustomer.customerName}`, 14, 40);
//     } else {
//       doc.text('Customer: All Customers', 14, 40);
//     }
    
//     // Add total amount
//     doc.text(`Total Amount: Rs ${totalAmount.toFixed(2)}`, 14, 50);
//   },
//   footer: (doc, yPosition) => {
//     doc.setFontSize(10);
//     doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, yPosition + 10);
//   }
// };

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4>Sales Report</h4>
                <h6>Manage Your Sales Report</h6>
              </div>
            </div>
            <ul className="table-top-head">
              {/* <TooltipIcons excelOptions={excelOptions} pdfOptions={pdfOptions}/>    */}
              <TooltipIcons 
  excelOptions={excelOptions} 
  pdfOptions={{
    ...pdfOptions,
    dateRange, // Pass the current dateRange state
    selectedCustomer, // Pass the current selectedCustomer
    totalAmount // Pass the current totalAmount
  }}
  
/>
              <RefreshIcon onClick={refetchTodaySaleOrders} />
              <CollapesIcon />
            </ul>
            {/* <div className="page-btn">
              <Link
                to="#"
                className="btn btn-primary"
                data-bs-toggle="modal"
                data-bs-target="#add-units"
              >
                <i className='ti ti-circle-plus me-1'></i>
                Add Sales
              </Link>
            </div> */}
          </div>

          <div className="card border-0">
            <div className="card-body pb-1">
              <form onSubmit={(e) => e.preventDefault()}>
                <div className="row align-items-end">
                  <div className="col-lg-10">
                    <div className="row">
                      <div className="col-md-4">
                        <div className="mb-3">
                          <label className="form-label">Date Range</label>
                          {isMobile ? (
                            <>
                              <DatePicker
                                className="form-control mb-2"
                                placeholder="Start Date"
                                onChange={(date) => setDateRange([date, dateRange?.[1]])}
                              />
                              <DatePicker
                                className="form-control"
                                placeholder="End Date"
                                onChange={(date) => setDateRange([dateRange?.[0], date])}
                              />
                            </>
                          ) : (
                            <DatePicker.RangePicker
                              className="form-control"
                              onChange={setDateRange}
                              style={{ width: '100%' }}
                            />
                          )}
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="mb-3">
                          <label className="form-label">Customer</label>
                          <Select
                            classNamePrefix="react-select"
                            options={customerOptions}
                            placeholder="All Customers"
                            value={customerOptions.find(opt => opt.value === selectedCustomer?._id)}
                            onChange={(selected) => setSelectedCustomer(selected ? customerResponse?.find(c => c._id === selected.value) : null)}
                            isClearable
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="mb-3">
                          <label className="form-label">Total Amount</label>
                          <Input
                            className="form-control"
                            placeholder="Total"
                            value={`Rs ${totalAmount.toFixed(2)}`}
                            readOnly
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-2">
                    <div className="mb-3">
                      <button 
                        className="btn btn-primary w-100" 
                        type="button"
                        onClick={() => {
                          setDateRange(null);
                          setSelectedCustomer(null);
                          setSelectedType(null);
                        }}
                      >
                        Reset Filters
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>

          <div className="card table-list-card">
            {/* <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <div className="search-set"></div>
              <div className="d-flex table-dropdown my-xl-auto right-content align-items-center flex-wrap row-gap-3">
                <div className="dropdown">
                  <Link
                    to="#"
                    className="dropdown-toggle btn btn-white btn-md d-inline-flex align-items-center"
                    data-bs-toggle="dropdown"
                  >
                    Payment Status
                  </Link>
                  <ul className="dropdown-menu dropdown-menu-end p-3">
                    <li><Link to="#" className="dropdown-item rounded-1">Paid</Link></li>
                    <li><Link to="#" className="dropdown-item rounded-1">Unpaid</Link></li>
                    <li><Link to="#" className="dropdown-item rounded-1">Overdue</Link></li>
                  </ul>
                </div>
              </div>
            </div> */}

            <div className="card-body">
              <div className="table-responsive">
                <Table 
                  columns={columns} 
                  dataSource={filteredData} 
                  expandable={expandable} 
                  loading={isTodaySaleOrderLoading}
                />
              </div>
            </div>
          </div>
        </div>
        <CommonFooter />
      </div>

      <AddSales 
        productResponse={productResponse}
        customerResponse={customerResponse}
        refetch={refetchTodaySaleOrders}
      />
      
      {showEditModal && (
        <EditSalesOrder
          salesOrder={saleOrderData}
          productlist={productResponse?.data}
          customersList={customerResponse}
          onUpdate={handleUpdateSalesOrder}
          onCancel={() => {
            setShowEditModal(false);
            setSelectedSaleOrderId(null);
          }}
          isUpdating={isUpdating}
          isLoading={isSaleOrderLoading}
        />
      )}

      <CommonDeleteModal 
        handleDelete={handleDelete}
      />
    </div>
  );
};

export default SaleReport;